import { useEffect, useRef, useState } from "react";
import { Viewer } from "@photo-sphere-viewer/core";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import { EquirectangularTilesAdapter } from "@photo-sphere-viewer/equirectangular-tiles-adapter";

import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";

import { interiorScenes as scenes, interiorTourOrder as tourOrder } from "../data/scenes";

export default function InteriorViewer({ sceneKey, setSceneKey }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const fadeRef = useRef(null);
  const autoTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  // ✅ continuous rotation refs
  const animationFrameRef = useRef(null);
  const rotationRef = useRef(true);

  const [isOpen, setIsOpen] = useState(false);

  const interiorScenesList = Object.entries(scenes);

  const capitalize = (str) =>
    str
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join("_");

  // =====================================================
  // CONTINUOUS 360 ROTATION
  // =====================================================

  const runCameraTour = () => {
    rotationRef.current = true;

    const rotate = () => {
      if (!viewerRef.current || !rotationRef.current)
        return;

      const position =
        viewerRef.current.getPosition();

      viewerRef.current.rotate({
        yaw: position.yaw + 0.001,
        pitch: position.pitch,
      });

      animationFrameRef.current =
        requestAnimationFrame(rotate);
    };

    rotate();
  };

  // =====================================================
  // VIEWER INIT
  // =====================================================

  useEffect(() => {
    if (!containerRef.current) return;

    // 🔥 destroy previous viewer
    if (viewerRef.current) {
      viewerRef.current.destroy();
      viewerRef.current = null;
    }

    const timeout = setTimeout(() => {
      viewerRef.current = new Viewer({
        container: containerRef.current,
        adapter: EquirectangularTilesAdapter,

        panorama: {
          width: 16384,
          cols: 32,
          rows: 16,

          baseUrl: `/images/tiles/${capitalize(sceneKey)}/preview.jpg`,

          levels: [
            { width: 512, cols: 1, rows: 1 },
            { width: 1024, cols: 2, rows: 1 },
            { width: 2048, cols: 4, rows: 2 },
            { width: 4096, cols: 8, rows: 4 },
            { width: 8192, cols: 16, rows: 8 },
            { width: 16384, cols: 32, rows: 16 },
          ],

          tileUrl: (col, row, level) => {
            return `/images/tiles/${capitalize(
              sceneKey
            )}/${level}/${col}_${row}.jpg`;
          },
        },

        navbar: ["zoom", "fullscreen"],

        // 🎯 START VIEW
        defaultYaw: scenes[sceneKey].defaultYaw || "0deg",
        defaultPitch: scenes[sceneKey].defaultPitch || "0deg",

        plugins: [[MarkersPlugin, {}]],
      });

      const markers = viewerRef.current.getPlugin(MarkersPlugin);

      loadHotspots(markers, sceneKey);

      viewerRef.current.addEventListener(
        "ready",
        () => {
          console.log(
            "✅ Scene loaded:",
            sceneKey
          );
        }
      );

      // =====================================================
      // HOTSPOT CLICK
      // =====================================================

      markers.addEventListener(
        "select-marker",
        (e) => {
          const hotspot =
            scenes[sceneKey].hotspots.find(
              (h) => h.id === e.marker.id
            );

          if (hotspot?.target) {
            triggerSceneChange(
              hotspot.target,
              hotspot
            );
          }
        }
      );

      // =====================================================
      // USER INTERACTION
      // =====================================================

      viewerRef.current.addEventListener(
        "user-interaction",
        () => {
          if (!viewerRef.current) return;

          console.log("⏸ Paused");

          // stop rotation
          rotationRef.current = false;

          if (animationFrameRef.current) {
            cancelAnimationFrame(
              animationFrameRef.current
            );
          }

          // clear timers
          if (autoTimerRef.current) {
            clearTimeout(autoTimerRef.current);
          }

          if (resumeTimerRef.current) {
            clearTimeout(resumeTimerRef.current);
          }

          // resume after idle
          resumeTimerRef.current = setTimeout(
            () => {
              if (!viewerRef.current) return;

              console.log("▶️ Resume");

              autoTimerRef.current =
                setTimeout(() => {
                  const hotspots =
                    scenes[sceneKey].hotspots;

                  const nextHotspot =
                    hotspots.find(
                      (h) => h.target
                    );

                  const currentIndex =
                    tourOrder.indexOf(sceneKey);

                  const nextScene =
                    tourOrder[
                    (currentIndex + 1) %
                    tourOrder.length
                    ];

                  triggerSceneChange(
                    nextScene,
                    nextHotspot
                  );
                }, 5000);
            },
            5000
          );
        }
      );

    }, 50);

    return () => {
      clearTimeout(timeout);

      rotationRef.current = false;

      if (animationFrameRef.current) {
        cancelAnimationFrame(
          animationFrameRef.current
        );
      }

      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
      }

      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
      }

      viewerRef.current?.destroy();
      viewerRef.current = null;
    };
  }, [sceneKey]);

  // =====================================================
  // SCENE TRANSITION
  // =====================================================

  const triggerSceneChange = async (
    nextScene,
    hotspot
  ) => {
    const viewer = viewerRef.current;
    const fadeEl = fadeRef.current;

    if (!viewer || !fadeEl) return;

    try {
      // stop rotation during transition
      rotationRef.current = false;

      if (animationFrameRef.current) {
        cancelAnimationFrame(
          animationFrameRef.current
        );
      }

      fadeEl.style.opacity = 1;

      await new Promise((res) =>
        setTimeout(res, 400)
      );

      setSceneKey(nextScene);

      const waitForNewScene = () =>
        new Promise((resolve) => {
          const check = () => {
            if (viewerRef.current) {
              viewerRef.current.addEventListener(
                "ready",
                resolve,
                {
                  once: true,
                }
              );
            } else {
              setTimeout(check, 50);
            }
          };

          check();
        });

      await waitForNewScene();

      const newViewer = viewerRef.current;

      if (!newViewer) return;

      newViewer.zoom(0);

      if (fadeRef.current) {
        fadeRef.current.style.opacity = 0;
      }
    } catch (e) {
      console.log("Transition interrupted");
    }
  };

  // =====================================================
  // HOTSPOTS
  // =====================================================

  const loadHotspots = (markers, sceneKey) => {
    markers.clearMarkers();

    scenes[sceneKey].hotspots.forEach((h) => {

      // Custom point label
      if (h.type === "point-label") {
        markers.addMarker({
          id: h.id,

          position: {
            yaw: h.yaw,
            pitch: h.pitch,
          },
          anchor: "center bottom",
          html: `<div class="road-marker expanded fixed-marker"
          style="
        --line-height:${h.lineHeight || 90}px;
      "
      >
            <div class="road-line"></div>

            <div class="road-dot">
                <div class="road-pulse"></div>
            </div>

          <div
  class="road-label ${h.direction === "right" ? "right" : ""}"
>
  ${h.label}
</div>

        </div>`,

        });

        return;
      }
      else if (h.type === "polygon") {

        markers.addMarker({
          id: h.id,

          polygon: h.polygon,

          svgStyle: h.svgStyle || {
            fill: "rgba(255, 193, 7, 0.35)",
            stroke: "#ffc107",
            strokeWidth: "3px",
          },
        });
      }
      else {
        let icon = "/icons/arrow.svg";

        markers.addMarker({
          id: h.id,

          position: {
            yaw: h.yaw,
            pitch: h.pitch,
          },

           html: `
          <div style="
          display:flex;
      flex-direction:column;
      align-items:center;
      justify-content:center;
      text-align:center;
        ">
              <img
            src=${icon} // or .svg
            style={{
              width: "10px",
              height: "10px",
              cursor: "pointer",
              filter: "drop-shadow(0 0 6px rgba(0,0,0,0.8))",
            }}
          />
           <div style="
          background: rgba(0,0,0,0.7);
          color: #fff;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
        ">
          ${h.label}
        </div>
          </div>
        `,

          anchor: "center center",
        });
      }
    });
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100vh", background: "#000" }}
      />

      {/* 🔥 Fade overlay */}
      <div
        ref={fadeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: "#000",
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 0.5s ease",
        }}
      />

      {/* 🏢 Stacked + Expand Interior Panel */}
      <div
        style={{
          position: "absolute",
          bottom: 40,
          right: 20,
          zIndex: 10,
        }}
      >
        {/* 📦 STACKED VIEW (collapsed) */}
        {!isOpen && (
          <div
            onClick={() => setIsOpen(true)}
            style={{
              position: "relative",
              width: 120,
              height: 80,
              cursor: "pointer",
            }}
          >
            {interiorScenesList.slice(0, 4).map(([key, s], i) => (
              <img
                key={key}
                src={s.thumbnail || s.panorama}
                alt=""
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 10,
                  top: i * -6,
                  left: i * 6,
                  opacity: 1 - i * 0.2,
                  zIndex: 10 - i,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>
        )}

        {/* 📂 EXPANDED LIST */}
        {isOpen && (
          <div
            className="hide-scrollbar"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background: "rgba(0,0,0,0.6)",
              padding: "10px",
              borderRadius: 12,
              backdropFilter: "blur(6px)",
              maxHeight: "80vh",
              overflowY: "auto",
              width: 130,
            }}
          >
            {/* ❌ Close button */}
            <div
              onClick={() => setIsOpen(false)}
              style={{
                alignSelf: "flex-end",
                cursor: "pointer",
                color: "#fff",
                fontSize: 18,
                marginBottom: 5,
              }}
            >
              ✕
            </div>

            {interiorScenesList.map(([key, s]) => (
              <div
                key={key}
                onClick={() => {
                  if (key === sceneKey) return;

                  const hotspot = scenes[sceneKey]?.hotspots?.find(
                    (h) => h.target === key
                  );

                  if (hotspot) {
                    triggerSceneChange(key, hotspot);
                  } else {
                    setSceneKey(key);
                  }
                }}
                style={{
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                {/* 🖼️ Image */}
                <img
                  src={s.thumbnail || s.panorama}
                  alt={s.name || key}
                  style={{
                    width: "100%",
                    height: 70,
                    objectFit: "cover",
                    borderRadius: 6,
                    border:
                      key === sceneKey
                        ? "2px solid #fff"
                        : "2px solid transparent",
                    opacity: key === sceneKey ? 1 : 0.7,
                  }}
                />

                {/* 🏷️ Name */}
                <div
                  style={{
                    marginTop: 4,
                    color: "#fff",
                    fontSize: 11,
                    wordBreak: "break-word",
                  }}
                >
                  {s.name || key}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}